import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import Icon from "../../components/Icon";
import { Sparkline, BarChart, Donut } from "../../components/Charts";
import { StatusBadge } from "../../components/Badges";
import LoadingBlock from "../../components/LoadingBlock";
import TaskDrawer from "../../components/TaskDrawer";
import DashboardTodosCard from "../../components/DashboardTodosCard";
import { useApi } from "../../hooks/useApi";
import { employeeTaskStats, listEmployeeTasks } from "../../api/employeeTaskService";
import { adminTicketStats } from "../../api/adminTicketService";
import { paginatedRows } from "../../utils/pagination";
import { formatDateShort, daysFromToday, initials } from "../../utils/formatters";
import { useAuth } from "../../contexts/AuthContext";
import { LEGEND_ENTRIES, STATUS_LABELS } from "../../utils/taskCalendar";

// Hex colors lined up with the calendar legend chips in index.css so the
// dashboard donut + bar match what users see on the calendar.
const CALENDAR_STATE_COLORS = {
  unassigned: "#94a3b8",          // slate
  assigned: "#6366f1",            // primary indigo
  "in-progress": "#d97706",       // warning amber
  "completed-unverified": "#10b981", // emerald
  verified: "#065f46",            // dark emerald
  cancelled: "#b91c1c",           // danger red
  overdue: "#ef4444",             // bright red — overlay on top of base state
};

const PRESETS = [
  { id: "7d", label: "Last 7 days", days: 6 },
  { id: "14d", label: "Last 14 days", days: 13 },
  { id: "30d", label: "Last 30 days", days: 29 },
  { id: "mtd", label: "This month", monthToDate: true },
  { id: "custom", label: "Custom" },
];

const isoDay = (d) => d.toISOString().slice(0, 10);

function defaultRangeFor(presetId) {
  const today = new Date();
  if (presetId === "mtd") {
    const first = new Date(today.getFullYear(), today.getMonth(), 1);
    return { from: isoDay(first), to: isoDay(today) };
  }
  const preset = PRESETS.find((p) => p.id === presetId);
  const days = preset?.days ?? 13;
  const start = new Date(today);
  start.setDate(today.getDate() - days);
  return { from: isoDay(start), to: isoDay(today) };
}

export default function DashboardPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [openTask, setOpenTask] = useState(null);
  const [preset, setPreset] = useState("14d");
  const [range, setRange] = useState(() => defaultRangeFor("14d"));

  const onPreset = (id) => {
    setPreset(id);
    if (id !== "custom") setRange(defaultRangeFor(id));
  };

  const stats = useApi(
    () => employeeTaskStats({ from: range.from, to: range.to }),
    [range.from, range.to],
  );
  const tStats = useApi(
    () => adminTicketStats({ from: range.from, to: range.to }),
    [range.from, range.to],
  );
  const overdue = useApi(() => listEmployeeTasks({ overdue: true, per_page: 6 }));
  const today = useApi(() => {
    const d = new Date().toISOString().slice(0, 10);
    return listEmployeeTasks({ due_from: d, due_to: d, per_page: 25 });
  });
  const upcoming = useApi(() => {
    const tomorrow = new Date(Date.now() + 86400000).toISOString().slice(0, 10);
    const wk = new Date(Date.now() + 7 * 86400000).toISOString().slice(0, 10);
    return listEmployeeTasks({ due_from: tomorrow, due_to: wk, per_page: 25 });
  });
  const s = stats.data?.data;
  const t = tStats.data?.data;
  const overdueRows = paginatedRows(overdue.data);
  const todayRows = paginatedRows(today.data).filter((tt) => tt.status !== "completed" && tt.status !== "cancelled");
  const upcomingRows = paginatedRows(upcoming.data).filter((tt) => tt.status !== "completed" && tt.status !== "cancelled");

  // Use the calendar-state breakdown so the dashboard mirrors what the
  // calendar legend shows. Falls back to the raw by_status splits if an
  // older backend is deployed and `by_calendar_state` isn't present yet.
  const statusSegs = useMemo(() => {
    if (!s) return [];
    const cal = s.by_calendar_state;
    if (cal && typeof cal === "object") {
      // Overdue is an overlay (a task can be e.g. in-progress AND overdue),
      // so showing it as its own bar intentionally double-counts. The card
      // subtitle calls that out so the bars aren't misread as a partition.
      // The donut on the right rail filters this out to keep a true
      // partition — see `partitionSegs` below.
      return LEGEND_ENTRIES.map((e) => ({
        label: STATUS_LABELS[e.state] || e.label,
        value: Number(cal[e.state] ?? 0),
        color: CALENDAR_STATE_COLORS[e.state] || "#94a3b8",
      }));
    }
    return [
      { label: "Completed", value: s.by_status?.completed ?? 0, color: "#059669" },
      { label: "In Progress", value: s.by_status?.in_progress ?? 0, color: "#0369a1" },
      { label: "Pending", value: s.by_status?.pending ?? 0, color: "#eab308" },
      { label: "Cancelled", value: s.by_status?.cancelled ?? 0, color: "#b91c1c" },
    ];
  }, [s]);

  // Donut needs a mutually-exclusive partition. Drop the overdue overlay
  // so slices sum to the actual task total.
  const partitionSegs = useMemo(
    () => statusSegs.filter((sg) => sg.label !== STATUS_LABELS.overdue),
    [statusSegs],
  );

  const series = useMemo(
    () => ({
      created: s?.series?.created || [],
      completed: s?.series?.completed || [],
      overdue: s?.series?.overdue || [],
      tickets: t?.series?.created || [],
    }),
    [s, t],
  );

  const openTicketsCount =
    (t?.by_status?.open ?? 0) + (t?.by_status?.in_progress ?? 0);

  return (
    <>
      <div className="page-head">
        <div>
          <h1 className="page-title">Welcome back, {user?.name?.split(" ")[0] || "there"}</h1>
          <div className="page-sub">
            {s ? `${s.overdue} overdue tasks need attention today.` : "Loading your firm overview…"}
          </div>
        </div>
        <div className="page-actions">
          <button className="btn btn-secondary"><Icon name="download" size={14} /> Export</button>
          <button className="btn btn-primary" onClick={() => navigate("/admin/ops")}>
            <Icon name="zap" size={14} /> Run task generator
          </button>
        </div>
      </div>

      <div className="filter-bar" style={{ marginBottom: 16 }}>
        {PRESETS.map((p) => (
          <button
            key={p.id}
            className={`chip ${preset === p.id ? "active" : ""}`}
            onClick={() => onPreset(p.id)}
          >
            {p.label}
          </button>
        ))}
        {preset === "custom" && (
          <>
            <input
              type="date"
              className="input"
              style={{ width: 150 }}
              value={range.from}
              max={range.to}
              onChange={(e) => setRange({ ...range, from: e.target.value })}
            />
            <span className="t-muted text-xs">to</span>
            <input
              type="date"
              className="input"
              style={{ width: 150 }}
              value={range.to}
              min={range.from}
              onChange={(e) => setRange({ ...range, to: e.target.value })}
            />
          </>
        )}
        <div className="t-muted text-xs" style={{ marginLeft: "auto" }}>
          {range.from} → {range.to}
        </div>
      </div>

      {/* KPI tiles reflow to 2x2 so the todos rail can sit beside them; both
          collapse to a single column on tablet and below. The loading block
          lives inside the left column so the rail renders without waiting on
          the (slower) task-stats call. */}
      <div className="grid-stack-300">
        <div>
          {stats.loading ? (
            <LoadingBlock rows={1} height={120} />
          ) : (
            <div className="kpi-grid kpi-grid-compact">
              <div className="kpi">
                <div className="kpi-label"><Icon name="check-square" size={12} /> Active tasks</div>
                <div className="kpi-value">{s?.total ?? 0}<small>in range</small></div>
                <div className="kpi-delta up"><Icon name="trending-up" size={11} /> {s?.upcoming_7_days ?? 0} this week</div>
                <div className="kpi-spark"><Sparkline data={series.created} color="#6366f1" /></div>
              </div>
              <div className="kpi">
                <div className="kpi-label" style={{ color: "var(--danger)" }}><Icon name="alert-triangle" size={12} /> Overdue</div>
                <div className="kpi-value">{s?.overdue ?? 0}</div>
                <div className="kpi-delta down">Resolve first</div>
                <div className="kpi-spark"><Sparkline data={series.overdue} color="#b91c1c" /></div>
              </div>
              <div className="kpi">
                <div className="kpi-label"><Icon name="check-circle" size={12} /> Completed</div>
                <div className="kpi-value">{s?.by_status?.completed ?? 0}</div>
                <div className="kpi-delta up"><Icon name="check" size={11} /> {s?.completed_unverified ?? 0} pending verify</div>
                <div className="kpi-spark"><Sparkline data={series.completed} color="#059669" /></div>
              </div>
              <div className="kpi">
                <div className="kpi-label"><Icon name="message-circle" size={12} /> Open tickets</div>
                <div className="kpi-value">{openTicketsCount}</div>
                <div className="kpi-delta"><Icon name="message-circle" size={11} /> {t?.unassigned ?? 0} unassigned</div>
                <div className="kpi-spark"><Sparkline data={series.tickets} color="#eab308" /></div>
              </div>
            </div>
          )}
        </div>

        <DashboardTodosCard />
      </div>

      <div className="spacer-lg" />

      <div className="grid-main-rail">
        <div className="card">
          <div className="card-head">
            <div>
              <div className="card-title">Tasks by status</div>
              <div className="card-sub">Across all clients · overdue overlaps with assigned / in-progress</div>
            </div>
          </div>
          <div className="card-body">
            <BarChart
              data={statusSegs.map((s2) => ({ label: s2.label, value: s2.value, color: s2.color }))}
              height={200}
            />
          </div>
        </div>
        <div className="card">
          <div className="card-head"><div><div className="card-title">Status snapshot</div><div className="card-sub">Live counts</div></div></div>
          <div className="card-body" style={{ display: "flex", gap: 16, alignItems: "center" }}>
            {partitionSegs.length > 0 && <Donut segments={partitionSegs} />}
            <div style={{ flex: 1, display: "flex", flexDirection: "column", gap: 8 }}>
              {statusSegs.map((sg) => (
                <div key={sg.label} style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 12 }}>
                  <span style={{ width: 8, height: 8, borderRadius: 2, background: sg.color }} />
                  <span style={{ flex: 1, color: "var(--ink-700)" }}>{sg.label}</span>
                  <span style={{ fontWeight: 600, color: "var(--ink-900)" }}>{sg.value}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      <div className="spacer-lg" />

      <div className="grid-2">
        <div className="card">
          <div className="card-head">
            <div>
              <div className="card-title" style={{ color: "var(--danger)" }}>Overdue · needs attention</div>
              <div className="card-sub">{overdueRows.length} of {s?.overdue ?? 0} shown</div>
            </div>
            <button className="btn btn-ghost btn-sm" onClick={() => navigate("/admin/oversight")}>
              View all <Icon name="arrow-right" size={12} />
            </button>
          </div>
          <div className="card-body no-pad">
            {overdue.loading ? (
              <div style={{ padding: 16 }}><LoadingBlock rows={3} height={32} /></div>
            ) : overdueRows.length === 0 ? (
              <div className="empty-state" style={{ padding: 24 }}>
                <div className="empty-state-title">Nothing overdue</div>
              </div>
            ) : (
              <table className="table">
                <thead><tr><th>Task</th><th>Business</th><th>Assignee</th><th className="t-num">Due</th></tr></thead>
                <tbody>
                  {overdueRows.map((t) => (
                    <tr key={t.id} className="clickable" onClick={() => setOpenTask(t.id)}>
                      <td className="t-primary">{t.task_master?.name}</td>
                      <td className="t-muted">{t.business?.business_name}</td>
                      <td>
                        {t.employee ? (
                          <div className="row">
                            <div className="avatar avatar-sm">{initials(t.employee.name)}</div>
                            <span className="t-muted">{t.employee.name?.split(" ")[0]}</span>
                          </div>
                        ) : (
                          <span className="badge badge-warning">Unassigned</span>
                        )}
                      </td>
                      <td className="t-num">
                        <span className="badge badge-danger">{Math.abs(daysFromToday(t.due_date))}d ago</span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>

        <div className="card">
          <div className="card-head">
            <div>
              <div className="card-title">Today &amp; this week</div>
              <div className="card-sub">{todayRows.length} due today · {upcomingRows.length} upcoming</div>
            </div>
            <button className="btn btn-ghost btn-sm" onClick={() => navigate("/admin/calendar")}>
              Calendar <Icon name="calendar" size={12} />
            </button>
          </div>
          <div className="card-body no-pad">
            <SectionHeader>Due today</SectionHeader>
            {todayRows.map((t) => (
              <TaskRow key={t.id} task={t} accent="warning" onClick={() => setOpenTask(t.id)} />
            ))}
            {todayRows.length === 0 && (
              <div style={{ padding: 16, fontSize: 13, color: "var(--ink-500)" }}>No tasks due today.</div>
            )}
            <SectionHeader>This week</SectionHeader>
            {upcomingRows.map((t) => (
              <TaskRow key={t.id} task={t} accent="primary" onClick={() => setOpenTask(t.id)} />
            ))}
          </div>
        </div>
      </div>

      <TaskDrawer
        taskId={openTask}
        role="admin"
        onClose={() => setOpenTask(null)}
        onChanged={() => { stats.reload(); tStats.reload(); overdue.reload(); today.reload(); upcoming.reload(); }}
      />
    </>
  );
}

function SectionHeader({ children }) {
  return (
    <div
      style={{
        padding: "8px 18px",
        fontSize: 11,
        fontWeight: 600,
        letterSpacing: 0.4,
        textTransform: "uppercase",
        color: "var(--ink-500)",
        borderTop: "1px solid var(--border)",
      }}
    >
      {children}
    </div>
  );
}

function TaskRow({ task, accent, onClick }) {
  const accentColor = accent === "warning" ? "var(--warning)" : "var(--primary-400)";
  return (
    <div
      className="clickable"
      onClick={onClick}
      style={{
        padding: "8px 18px",
        display: "flex",
        alignItems: "center",
        gap: 10,
        borderTop: "1px solid var(--border)",
        cursor: "pointer",
      }}
    >
      <div style={{ width: 4, height: 28, borderRadius: 2, background: accentColor }} />
      <div style={{ flex: 1, minWidth: 0 }}>
        <div className="t-primary" style={{ fontSize: 13, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
          {task.task_master?.name}
        </div>
        <div style={{ fontSize: 11, color: "var(--ink-500)" }}>
          {task.business?.business_name}
          {task.due_date && ` · ${formatDateShort(task.due_date)}`}
        </div>
      </div>
      <StatusBadge status={task.status} />
    </div>
  );
}
